import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DocumentTemplateSelector from './DocumentTemplateSelector';
import { apiFetch } from './utils/api';
import { generateDocumentOutput } from './utils/documentGeneration';
import { getCompanyVariables } from './utils/templateVariables';

function PPMQuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [quote, setQuote] = useState(null);
  const [formData, setFormData] = useState({
    quote_ref: '',
    customer_id: '',
    site_id: '',
    contact_name: '',
    automatic_doors: 0,
    manual_doors: 0,
    automatic_gates: 0,
    automatic_barriers: 0,
    automatic_shutters: 0,
    visits_per_year: 1,
    contract_years: 1,
    notes: ''
  });

  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    fetchCustomers();
    fetchSites();
    fetchPricing();

    if (!isNew) {
      fetchQuote();
    } else {
      generateQuoteRef();
      setLoading(false);
    }
  }, [id]);

  // Filter sites when customer changes
  useEffect(() => {
    if (formData.customer_id) {
      const customerSites = sites.filter(s => s.customer_id === parseInt(formData.customer_id));
      setFilteredSites(customerSites);
      if (formData.site_id && !customerSites.find(s => s.id === parseInt(formData.site_id))) {
        setFormData(prev => ({ ...prev, site_id: '' }));
      }
    } else {
      setFilteredSites([]);
      setFormData(prev => ({ ...prev, site_id: '' }));
    }
  }, [formData.customer_id, sites]);

  // Recalculate when any value changes
  useEffect(() => {
    if (pricing) {
      calculateTotals();
    }
  }, [
    formData.automatic_doors,
    formData.manual_doors,
    formData.automatic_gates,
    formData.automatic_barriers,
    formData.automatic_shutters,
    formData.visits_per_year,
    formData.contract_years,
    pricing
  ]);

  const generateQuoteRef = async () => {
    // For now, generate a simple ref. Could use backend endpoint later
    const ref = `PPM-Q-${Date.now().toString().slice(-6)}`;
    setFormData(prev => ({ ...prev, quote_ref: ref }));
  };

  const fetchCustomers = async () => {
    try {
      const response = await apiFetch('/api/customers');
      const data = await response.json();
      setCustomers(data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await apiFetch('/api/sites');
      const data = await response.json();
      setSites(data);
    } catch (err) {
      console.error('Error fetching sites:', err);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await apiFetch('/api/ppm/pricing-config');
      const data = await response.json();
      setPricing(data);
    } catch (err) {
      console.error('Error fetching pricing:', err);
    }
  };

  const fetchQuote = async () => {
    try {
      const response = await apiFetch(`/api/ppm/quotes/${id}`);
      const data = await response.json();
      setQuote(data);
      setFormData({
        quote_ref: data.quote_ref || '',
        customer_id: data.customer_id || '',
        site_id: data.site_id || '',
        contact_name: data.contact_name || '',
        automatic_doors: data.automatic_doors || 0,
        manual_doors: data.manual_doors || 0,
        automatic_gates: data.automatic_gates || 0,
        automatic_barriers: data.automatic_barriers || 0,
        automatic_shutters: data.automatic_shutters || 0,
        visits_per_year: data.visits_per_year || 1,
        contract_years: data.contract_years || 1,
        notes: data.notes || ''
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching quote:', err);
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    if (!pricing) return;

    const first_asset_rate = parseFloat(pricing.base_rate || 220); // First automatic asset includes travel

    // Count total automatic assets (doors, gates, barriers, shutters)
    const total_automatic_assets =
      formData.automatic_doors +
      formData.automatic_gates +
      formData.automatic_barriers +
      formData.automatic_shutters;

    // Calculate automatic asset costs
    let automatic_cost = 0;
    if (total_automatic_assets > 0) {
      // First automatic asset costs £220 (includes travel)
      automatic_cost = first_asset_rate;

      // Additional doors at £50 each
      if (formData.automatic_doors > 1) {
        automatic_cost += (formData.automatic_doors - 1) * parseFloat(pricing.automatic_door_rate || 50);
      }
      // If no doors but other automatic assets, the "first" is already counted
      // Additional gates/barriers/shutters at £70 each
      const additionalGates = formData.automatic_doors > 0 ? formData.automatic_gates : Math.max(0, formData.automatic_gates - 1);
      const additionalBarriers = (formData.automatic_doors + formData.automatic_gates) > 0 ? formData.automatic_barriers : Math.max(0, formData.automatic_barriers - 1);
      const additionalShutters = (formData.automatic_doors + formData.automatic_gates + formData.automatic_barriers) > 0 ? formData.automatic_shutters : Math.max(0, formData.automatic_shutters - 1);

      automatic_cost += additionalGates * parseFloat(pricing.automatic_gate_rate || 70);
      automatic_cost += additionalBarriers * parseFloat(pricing.automatic_barrier_rate || 70);
      automatic_cost += additionalShutters * parseFloat(pricing.automatic_shutter_rate || 70);
    }

    // Manual doors always at £30 each (never get the first-asset rate)
    const manual_cost = formData.manual_doors * parseFloat(pricing.manual_door_rate || 30);

    const cost_per_visit = automatic_cost + manual_cost;
    const annual_cost = cost_per_visit * formData.visits_per_year;

    let discount_percent = 0;
    if (formData.contract_years === 2) discount_percent = parseFloat(pricing.discount_2yr || 2.5);
    else if (formData.contract_years === 3) discount_percent = parseFloat(pricing.discount_3yr || 5.0);
    else if (formData.contract_years === 4) discount_percent = parseFloat(pricing.discount_4yr || 7.5);
    else if (formData.contract_years === 5) discount_percent = parseFloat(pricing.discount_5yr || 10.0);

    if (formData.visits_per_year === 2) {
      discount_percent += parseFloat(pricing.bi_annual_bonus || 2.5);
    }

    const total_before_discount = annual_cost * formData.contract_years;
    const discount_amount = total_before_discount * (discount_percent / 100);
    const total_payable = total_before_discount - discount_amount;
    const average_annual = total_payable / formData.contract_years;

    setCalculation({
      first_asset_rate,
      automatic_cost,
      manual_cost,
      cost_per_visit,
      annual_cost,
      discount_percent,
      total_before_discount,
      discount_amount,
      total_payable,
      average_annual,
      total_automatic_assets
    });
  };

  const handleSave = async () => {
    if (!formData.quote_ref || !formData.customer_id) {
      alert('Please fill in Quote Ref and Customer');
      return;
    }

    setSaving(true);
    setSaved(false);

    try {
      const url = isNew
        ? '/api/ppm/quotes'
        : `/api/ppm/quotes/${id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save quote');

      const savedData = await response.json();

      if (isNew) {
        navigate(`/ppm-quotes/${savedData.id}`);
      } else {
        setQuote(savedData);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      console.error('Error saving quote:', err);
      alert('Failed to save quote: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const prepareQuoteData = async (quote) => {
    const customer = customers.find(c => c.id === parseInt(quote.customer_id));
    const site = sites.find(s => s.id === parseInt(quote.site_id));

    // Fetch company settings
    let companySettings = {};
    try {
      const res = await apiFetch('/api/company-settings');
      companySettings = await res.json();
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }

    return {
      quote_ref: quote.quote_ref,
      date: new Date().toLocaleDateString('en-GB'),
      customer_name: customer?.company_name || '',
      customer_address: customer?.address || '',
      customer_postcode: customer?.postcode || '',
      site_name: site?.site_name || '',
      site_address: site?.address || '',
      site_postcode: site?.postcode || '',
      contact_name: quote.contact_name || '',
      automatic_doors: quote.automatic_doors || 0,
      manual_doors: quote.manual_doors || 0,
      automatic_gates: quote.automatic_gates || 0,
      automatic_barriers: quote.automatic_barriers || 0,
      automatic_shutters: quote.automatic_shutters || 0,
      visits_per_year: quote.visits_per_year || 1,
      contract_years: quote.contract_years || 1,
      annual_cost: calculation?.annual_cost || 0,
      total_payable: calculation?.total_payable || 0,
      discount_percent: calculation?.discount_percent || 0,
      notes: quote.notes || '',

      // Company fields (consistent across all templates)
      ...getCompanyVariables(companySettings)
    };
  };

  const handlePrint = async () => {
    if (!selectedTemplate) {
      setShowTemplateSelector(true);
      return;
    }
    const outputWindow = window.open('', '_blank');
    if (outputWindow) {
      outputWindow.document.write('<p style="font-family: sans-serif; padding: 20px;">Preparing document...</p>');
    }
    await handleTemplateGenerate(selectedTemplate, 'print', null, outputWindow);
  };

  const handleDownloadPDF = async () => {
    if (!selectedTemplate) {
      setShowTemplateSelector(true);
      return;
    }
    await handleTemplateGenerate(selectedTemplate, 'pdf');
  };

  const handleTemplateGenerate = async (template, action, data, outputWindow = null) => {
    try {
      if (action === 'email') {
        setSelectedTemplate(template);
        setShowTemplateSelector(false);
        handleEmailClick();
        return;
      }

      const templateData = await prepareQuoteData(formData);
      setSelectedTemplate(template);
      await generateDocumentOutput(template, action, templateData, `PPM-Quote-${formData.quote_ref || id}`, outputWindow);
      setShowTemplateSelector(false);
    } catch (error) {
      console.error('Document generation error:', error);
      alert('Error generating document: ' + error.message);
    }
  };

  const handleEmailClick = () => {
    setEmailTo(selectedCustomer?.email || '');
    setEmailSubject(`PPM Quote ${formData.quote_ref}`);
    setEmailBody(`Dear ${selectedCustomer?.company_name},\n\nPlease find attached your PPM Quote.\n\nBest regards`);
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailTo) {
      alert('Please enter recipient email');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await apiFetch(`/api/ppm/quotes/${id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailTo,
          subject: emailSubject,
          body: emailBody
        })
      });

      if (response.ok) {
        alert('Email sent successfully!');
        setShowEmailModal(false);
      } else {
        alert('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading quote...</div>
      </div>
    );
  }

  const selectedCustomer = customers.find(c => c.id === parseInt(formData.customer_id));
  const selectedSite = sites.find(s => s.id === parseInt(formData.site_id));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/ppm-quotes')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">
                {isNew ? 'New PPM Quote' : formData.quote_ref || 'PPM Quote'}
              </h1>
              {selectedCustomer && (
                <p className="text-sm text-blue-100 mt-1">
                  {selectedCustomer.company_name}
                  {selectedSite && ` → ${selectedSite.site_name}`}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isNew && (
                <>
                  <button
                    onClick={() => setShowTemplateSelector(true)}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                    title={selectedTemplate ? `Template: ${selectedTemplate.name}` : 'Select Template'}
                  >
                    📋 {selectedTemplate ? selectedTemplate.name : 'Template'}
                  </button>
                  <button
                    onClick={handlePrint}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    🖨️ Print
                  </button>
                  <button
                    onClick={handleDownloadPDF}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    📄 PDF
                  </button>
                  <button
                    onClick={handleEmailClick}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    📧 Email
                  </button>
                </>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                💾 {saving ? 'Saving...' : 'Save'}
              </button>
              {saved && (
                <span className="text-sm text-white font-medium">✓ Saved</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-4">

            {/* Customer & Site */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer & Site</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quote Reference *
                  </label>
                  <input
                    type="text"
                    value={formData.quote_ref}
                    onChange={(e) => setFormData({ ...formData, quote_ref: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="PPM-Q-123456"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Mr. Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer *
                  </label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Select Customer</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site
                  </label>
                  <select
                    value={formData.site_id}
                    onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
                    disabled={!formData.customer_id}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">
                      {formData.customer_id ? 'Select Site (Optional)' : 'Select Customer First'}
                    </option>
                    {filteredSites.map(site => (
                      <option key={site.id} value={site.id}>
                        {site.site_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Equipment Quantities */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Equipment Quantities</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'automatic_doors', label: 'Automatic Doors', rate: pricing?.automatic_door_rate || 50 },
                  { key: 'manual_doors', label: 'Manual Doors', rate: pricing?.manual_door_rate || 30 },
                  { key: 'automatic_gates', label: 'Automatic Gates', rate: pricing?.automatic_gate_rate || 70 },
                  { key: 'automatic_barriers', label: 'Automatic Barriers', rate: pricing?.automatic_barrier_rate || 70 },
                  { key: 'automatic_shutters', label: 'Automatic Shutters', rate: pricing?.automatic_shutter_rate || 70 }
                ].map(({ key, label, rate }) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {label}
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="0"
                        value={formData[key]}
                        onChange={(e) => setFormData({ ...formData, [key]: parseInt(e.target.value) || 0 })}
                        className="w-20 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <span className="text-sm text-gray-500">
                        @ £{parseFloat(rate || 0).toFixed(2)} each
                        {formData[key] > 0 && (
                          <span className="ml-2 font-medium text-gray-700">
                            = £{(formData[key] * parseFloat(rate || 0)).toFixed(2)}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contract Terms */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contract Terms</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visits Per Year
                  </label>
                  <select
                    value={formData.visits_per_year}
                    onChange={(e) => setFormData({ ...formData, visits_per_year: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="1">1x Service Visit Per Year</option>
                    <option value="2">2x Service Visits Per Year (+{pricing?.bi_annual_bonus || 2.5}% discount)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Length
                  </label>
                  <select
                    value={formData.contract_years}
                    onChange={(e) => setFormData({ ...formData, contract_years: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="1">1 Year (No Contract Discount)</option>
                    <option value="2">2 Years ({pricing?.discount_2yr || 2.5}% discount)</option>
                    <option value="3">3 Years ({pricing?.discount_3yr || 5}% discount)</option>
                    <option value="4">4 Years ({pricing?.discount_4yr || 7.5}% discount)</option>
                    <option value="5">5 Years ({pricing?.discount_5yr || 10}% discount)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <textarea
                rows="4"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Additional notes or special terms..."
              />
            </div>
          </div>

          {/* Sidebar - Calculation Preview */}
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              {calculation && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Quote Calculation</h3>

                  <div className="space-y-3 text-sm">
                    {calculation.total_automatic_assets > 0 && (
                      <>
                        <div className="flex justify-between pb-2 border-b">
                          <span className="text-gray-600">1st Asset (inc. travel):</span>
                          <span className="font-medium">£{parseFloat(calculation.first_asset_rate || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between pb-2 border-b">
                          <span className="text-gray-600">Additional Automatic:</span>
                          <span className="font-medium">£{parseFloat((calculation.automatic_cost || 0) - (calculation.first_asset_rate || 0)).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    {formData.manual_doors > 0 && (
                      <div className="flex justify-between pb-2 border-b">
                        <span className="text-gray-600">Manual Doors:</span>
                        <span className="font-medium">£{parseFloat(calculation.manual_cost || 0).toFixed(2)}</span>
                      </div>
                    )}

                    <div className="flex justify-between pb-2 border-b border-gray-300">
                      <span className="text-gray-700 font-medium">Cost Per Visit:</span>
                      <span className="font-semibold">£{parseFloat(calculation.cost_per_visit || 0).toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between pb-2">
                      <span className="text-gray-600">Annual Cost:</span>
                      <span className="font-medium">£{parseFloat(calculation.annual_cost || 0).toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between pb-2">
                      <span className="text-gray-600">Contract Length:</span>
                      <span className="font-medium">{formData.contract_years} {formData.contract_years === 1 ? 'Year' : 'Years'}</span>
                    </div>

                    <div className="flex justify-between pb-2 border-b border-gray-300">
                      <span className="text-gray-600">Total Before Discount:</span>
                      <span className="font-medium">£{parseFloat(calculation.total_before_discount || 0).toFixed(2)}</span>
                    </div>

                    {calculation.discount_percent > 0 && (
                      <>
                        <div className="flex justify-between pb-2">
                          <span className="text-green-600">Discount ({parseFloat(calculation.discount_percent || 0).toFixed(1)}%):</span>
                          <span className="font-medium text-green-600">-£{parseFloat(calculation.discount_amount || 0).toFixed(2)}</span>
                        </div>
                      </>
                    )}

                    <div className="pt-3 border-t-2 border-indigo-200">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-900 font-semibold text-base">Total Payable:</span>
                        <span className="text-2xl font-bold text-teal-600">
                          £{parseFloat(calculation.total_payable || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-2 text-right">
                        Average £{parseFloat(calculation.average_annual || 0).toFixed(2)}/year
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <DocumentTemplateSelector
          isOpen={showTemplateSelector}
          onClose={() => setShowTemplateSelector(false)}
          documentType="ppm_quote"
          placementKey="ppm_quote.print_pdf"
          data={formData}
          onGenerate={handleTemplateGenerate}
        />
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">📧 Email PPM Quote</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="customer@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
              >
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PPMQuoteDetail;
